// src/services/hrDashboardService.js
// FuMind_GM HR - Read-Only Dashboard Service
import { getHRSettingsMasterData } from "./employeeService";

const HR_DASHBOARD_LOAD_FLOW_URL = "/tracka-flow/v1/HR_DASHBOARD_LOAD";
const HR_DASHBOARD_API_KEY = "";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return safeText(value.Value ?? value.value ?? value.Title ?? value.DisplayName ?? value.Email ?? "", fallback);
  return String(value).trim() || fallback;
}

function safeJson(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  const parsed = safeJson(value, []);
  return Array.isArray(parsed) ? parsed : [];
}

function asObject(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  const parsed = safeJson(value, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
}

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeDashboardRole(role) {
  const x = String(role || "").toLowerCase().trim();
  if (x.includes("hradmin") || x === "hr admin") return "HRAdmin";
  if (x === "admin" || x.includes("administrator")) return "Admin";
  if (x === "hr") return "HR";
  if (x.includes("manager")) return "Manager";
  return "Employee";
}

export function normalizeDashboardUser(user = {}) {
  const employeeId = user.employeeId || user.employeeCode || user.EmployeeID || user.EmployeeCode || "";
  const email = user.email || user.workEmail || user.Email || user.WorkEmail || "";
  return {
    employeeId,
    employeeCode: user.employeeCode || employeeId,
    workEmail: user.workEmail || email,
    email,
    displayName: user.displayName || user.name || user.employeeName || user.EmployeeName || employeeId || email || "Employee",
    role: normalizeDashboardRole(user.role || user.Role || user.userRole || "Employee"),
  };
}

function normalizeKpis(k = {}) {
  return {
    activeEmployees: num(k.activeEmployees),
    onLeaveToday: num(k.onLeaveToday),
    pendingApprovals: num(k.pendingApprovals),
    noticePeriodEmployees: num(k.noticePeriodEmployees),
    exitClearancesOverdue: num(k.exitClearancesOverdue),
    unreadNotifications: num(k.unreadNotifications),
  };
}

function normalizeTodayOperations(x = {}) {
  return {
    presentToday: num(x.presentToday),
    absentToday: num(x.absentToday),
    lateOrMissing: num(x.lateOrMissing),
    regularizationPending: num(x.regularizationPending),
    attendanceHealthPercent: num(x.attendanceHealthPercent),
    leaveCoveragePercent: num(x.leaveCoveragePercent),
  };
}

function normalizePeopleMovement(x = {}) {
  return {
    newJoiners: num(x.newJoiners),
    onboardingPending: num(x.onboardingPending),
    noticePeriodEmployees: num(x.noticePeriodEmployees),
    exitClearancesPending: num(x.exitClearancesPending),
    exitClearancesOverdue: num(x.exitClearancesOverdue),
    readyForRelieving: num(x.readyForRelieving),
  };
}

function normalizeActionItem(x = {}) {
  return {
    id: safeText(x.id || x.ID || x.referenceId),
    title: safeText(x.title || x.action || x.activityType, "Pending action"),
    employeeId: safeText(x.employeeId || x.EmployeeId),
    employeeName: safeText(x.employeeName || x.EmployeeName),
    module: safeText(x.module || x.sourceModule),
    status: safeText(x.status || x.activityStatus || x.priority, "Pending"),
    route: safeText(x.route),
    tone: safeText(x.tone),
  };
}

function normalizeRisk(x = {}) {
  return {
    id: safeText(x.id || x.ID || x.referenceId),
    title: safeText(x.title || x.alert, "Risk item"),
    message: safeText(x.message || x.description, "Review required."),
    status: safeText(x.status || x.severity, "Pending"),
    route: safeText(x.route),
  };
}

function normalizeActivity(x = {}) {
  return {
    activityId: safeText(x.activityId || x.ActivityId || x.ID),
    employeeId: safeText(x.employeeId || x.EmployeeId),
    employeeName: safeText(x.employeeName || x.EmployeeName),
    workEmail: safeText(x.workEmail || x.WorkEmail),
    activityType: safeText(x.activityType || x.ActivityType || x.title, "Activity"),
    activityStatus: safeText(x.activityStatus || x.ActivityStatus || x.status),
    activityDate: safeText(x.activityDate || x.ActivityDate || x.Created || x.Modified),
  };
}

function normalizeTrendRows(rows) {
  return asArray(rows).map((x, i) => ({ label: safeText(x.label || x.month || x.name, `M${i + 1}`), value: num(x.value || x.count) }));
}

function firstArray(...values) {
  for (const value of values) {
    const rows = asArray(value);
    if (rows.length) return rows;
  }
  return [];
}
function fieldValue(row = {}, ...names) {
  for (const name of names) {
    if (row?.[name] !== undefined && row?.[name] !== null) {
      const value = safeText(row[name]);
      if (value) return value;
    }
  }
  const lookup = Object.keys(row || {}).reduce((out, key) => {
    out[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = row[key];
    return out;
  }, {});
  for (const name of names) {
    const value = safeText(lookup[String(name).toLowerCase().replace(/[^a-z0-9]/g, "")]);
    if (value) return value;
  }
  return "";
}
function uniqueValues(rows, ...fields) {
  return Array.from(new Set(asArray(rows).map((row) => fieldValue(row, ...fields)).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}
function normalizeFilterOptions(raw = {}, fallback = {}) {
  const source = asObject(raw);
  const settings = asObject(fallback);
  const countries = firstArray(settings.countries, settings.Countries);
  const legalEntities = firstArray(settings.legalEntities, settings.LegalEntities);
  const locations = firstArray(settings.locations, settings.Locations);
  const departments = firstArray(settings.departments, settings.Departments);
  const workUnits = firstArray(settings.workUnits, settings.WorkUnits, settings.productionUnits, settings.ProductionUnits);
  const employmentTypes = firstArray(settings.employmentTypes, settings.EmploymentTypes);
  const industrySources = [...workUnits, ...legalEntities, ...locations, ...firstArray(settings.leavePolicies, settings.LeavePolicies)];
  return {
    countryCodes: firstArray(source.countryCodes, source.countries).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.countryCodes, source.countries).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(countries, "CountryCode", "countryCode", "ISOAlpha2", "Code", "Title"),
    legalEntityCodes: firstArray(source.legalEntityCodes, source.legalEntities).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.legalEntityCodes, source.legalEntities).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(legalEntities, "LegalEntityCode", "legalEntityCode", "Code", "Title"),
    locationCodes: firstArray(source.locationCodes, source.locations).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.locationCodes, source.locations).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(locations, "LocationCode", "locationCode", "Code", "Title"),
    departmentCodes: firstArray(source.departmentCodes, source.departments).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.departmentCodes, source.departments).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(departments, "DepartmentCode", "departmentCode", "Code", "Title"),
    workUnitCodes: firstArray(source.workUnitCodes, source.workUnits, source.productionUnits).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.workUnitCodes, source.workUnits, source.productionUnits).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(workUnits, "WorkUnitCode", "workUnitCode", "ProductionUnitCode", "Code", "Title"),
    industryTypes: firstArray(source.industryTypes).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.industryTypes).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(industrySources, "IndustryType", "industryType", "IndustryTypeName"),
    employmentTypes: firstArray(source.employmentTypes).map((x) => safeText(x)).filter(Boolean).length
      ? firstArray(source.employmentTypes).map((x) => safeText(x)).filter(Boolean)
      : uniqueValues(employmentTypes, "EmploymentTypeCode", "employmentTypeCode", "EmploymentTypeName", "Title"),
  };
}
function normalizeDashboard(data = {}) {
  const raw = asObject(data.dashboard || data.data || data);
  const trends = asObject(raw.trends);
  return {
    success: data.success !== false,
    generatedOn: safeText(raw.generatedOn || data.generatedOn || new Date().toISOString()),
    kpis: normalizeKpis(asObject(raw.kpis)),
    todayOperations: normalizeTodayOperations(asObject(raw.todayOperations)),
    peopleMovement: normalizePeopleMovement(asObject(raw.peopleMovement)),
    actionItems: asArray(raw.actionItems).map(normalizeActionItem),
    risks: asArray(raw.risks).map(normalizeRisk),
    recentActivity: asArray(raw.recentActivity).map(normalizeActivity),
    trends: {
      leaveRequestsByMonth: normalizeTrendRows(trends.leaveRequestsByMonth),
      exitTrendByMonth: normalizeTrendRows(trends.exitTrendByMonth),
    },
    filterOptions: normalizeFilterOptions(raw.filterOptions || data.filterOptions),
    filteredEmployeeCount: num(raw.filteredEmployeeCount ?? data.filteredEmployeeCount),
    raw,
  };
}

async function postJson(url, body) {
  if (!url || url.includes("PASTE_")) {
    return normalizeDashboard({ success: true, dashboard: { generatedOn: new Date().toISOString() } });
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({  ...body }),
  });
  const text = await res.text();
  const data = safeJson(text, { raw: text });
  if (!res.ok) throw new Error(data.message || data.error || text || `Dashboard flow failed (${res.status})`);
  if (data.success === false) throw new Error(data.message || "Dashboard flow returned failure.");
  return normalizeDashboard(data);
}

export async function loadHRDashboard({ currentUser, payload = {} }) {
  const dashboard = await postJson(HR_DASHBOARD_LOAD_FLOW_URL, {
    action: "loadDashboard",
    currentUser: normalizeDashboardUser(currentUser),
    payload,
  });
  const existing = normalizeFilterOptions(dashboard.filterOptions);
  const hasFilters = Object.values(existing).some((rows) => Array.isArray(rows) && rows.length > 0);
  if (hasFilters) return { ...dashboard, filterOptions: existing };
  try {
    const settings = await getHRSettingsMasterData();
    return { ...dashboard, filterOptions: normalizeFilterOptions(dashboard.filterOptions, settings) };
  } catch (error) {
    return {
      ...dashboard,
      filterOptions: existing,
      filterOptionsWarning: error?.message || "HR Settings filter master data is unavailable.",
    };
  }
}
